import React, { useCallback, useRef, useState, type SyntheticEvent } from 'react';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { Anchor, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import messages from './messages.tsx';

type StopFunction = () => void;
type Nudge = { stop?: StopFunction };

const TARGET = 'JSM Project settings Nav3';
const MESSAGE_ID = 'jsm-simplified-project-settings-nav3';

const Nudge = ({ stop }: Nudge) => {
	const { formatMessage } = useIntl();
	const [isSpotlightActive, setIsSpotlightActive] = useState<boolean>(true);
	const targetNode = useRef<HTMLElement>(
		globalThis.document?.querySelector(
			'[data-testid="ContextualNavigation-resize-children-wrapper"]',
		),
	);

	const onClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireTrackAnalytics(analyticsEvent, 'jsmProjectSettingsNav3Changeboarding dismissed');
			stop?.();
			setIsSpotlightActive(false);
		},
		[stop],
	);

	const LEARN_MORE_LINK =
		'https://community.atlassian.com/t5/Jira-Service-Management-articles/Updated-project-settings-navigation-in-Jira-Service-Management/ba-p/2829019';

	if (!isSpotlightActive) {
		return <></>;
	}

	return (
		<ContextualAnalyticsData sourceName="JSMProjectSettingsNav3Changeboarding" sourceType="Screen">
			<SpotlightTransition>
				<JiraSpotlight
					actions={[
						{
							onClick,
							text: formatMessage(messages.ok),
						},
					]}
					heading={formatMessage(messages.title)}
					targetNode={targetNode.current ?? undefined}
					key={TARGET}
					targetRadius={3}
					targetBgColor={token('elevation.surface')}
					dialogWidth={300}
					dialogPlacement="left middle"
					messageId="navigation-apps-sidebar-servicedesk.common.ui.settings-changeboarding.jira-spotlight"
					messageType="engagement"
				>
					{formatMessage(messages.description)}
					<br />
					<Anchor href={LEARN_MORE_LINK} target="_blank" xcss={anchorStyles}>
						{formatMessage(messages.readMoreLink)}
					</Anchor>
				</JiraSpotlight>
			</SpotlightTransition>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const ProjectSettingsChangeBoarding = () => (
	<CoordinationClient messageId={MESSAGE_ID} messageType="engagement">
		<Nudge />
	</CoordinationClient>
);

export default ProjectSettingsChangeBoarding;

const anchorStyles = xcss({
	color: 'color.text.inverse',
	textDecoration: 'underline',
	borderStyle: 'none',

	':link': {
		color: 'color.text.inverse',
	},
	':hover': {
		textDecoration: 'none',
		color: 'color.text.inverse',
	},
	':active': {
		color: 'color.text.inverse',
	},
	':visited': {
		color: 'color.text.inverse',
	},
	':visited:active': {
		color: 'color.text.inverse',
	},
});
